import type { ToolPermissionPreHook, ToolPermissionPreHookAction } from '@/api/agent.api';
import type { AiApprovalFormState, ToolPermissionPreHookFormState } from './agentFormTypes';

export const TOOL_PRE_HOOK_ACTION_OPTIONS: { value: ToolPermissionPreHookAction; label: string }[] =
  [
    { value: 'TOOL_PERMISSION_PRE_HOOK_ACTION_REQUIRE_HUMAN', label: '审批' },
    { value: 'TOOL_PERMISSION_PRE_HOOK_ACTION_DENY', label: '拒绝' },
    { value: 'TOOL_PERMISSION_PRE_HOOK_ACTION_AUTO_APPROVE', label: '自动批准' },
    // FALLTHROUGH：本钩子不下结论，交给下一个钩子；全部继续判定后回落到 deny / allow / 默认行为
    { value: 'TOOL_PERMISSION_PRE_HOOK_ACTION_FALLTHROUGH', label: '继续判定' },
  ];

/** 判定失败（服务异常）时，以及 AI 判定为 False 时都不能自动批准 */
export const TOOL_PRE_HOOK_NO_AUTO_APPROVE_OPTIONS = TOOL_PRE_HOOK_ACTION_OPTIONS.filter(
  (o) => o.value !== 'TOOL_PERMISSION_PRE_HOOK_ACTION_AUTO_APPROVE'
);

export function toolPreHookActionLabel(action: ToolPermissionPreHookAction | '' | undefined) {
  return TOOL_PRE_HOOK_ACTION_OPTIONS.find((o) => o.value === action)?.label;
}

/** AI 审批钩子的 name，与 type 一样固定为 ai */
export const AI_PRE_HOOK_NAME = 'ai';

export const EMPTY_TOOL_PRE_HOOK: ToolPermissionPreHookFormState = {
  name: '',
  toolMatch: '',
  endpointUrl: '',
  onTrue: '',
  onFalse: '',
  onError: '',
  timeout: 0,
  retries: 0,
};

/** AI 审批默认关闭；动作按后端建议预填：判定为 True 自动批准，False 和判定失败转人工审批 */
export const DEFAULT_AI_APPROVAL: AiApprovalFormState = {
  enabled: false,
  toolMatch: '',
  prompt: '',
  onTrue: 'TOOL_PERMISSION_PRE_HOOK_ACTION_AUTO_APPROVE',
  onFalse: 'TOOL_PERMISSION_PRE_HOOK_ACTION_REQUIRE_HUMAN',
  onError: 'TOOL_PERMISSION_PRE_HOOK_ACTION_REQUIRE_HUMAN',
  timeout: 0,
  retries: 0,
};

/** HTTP 钩子与 AI 审批共有的判定字段 */
type JudgeFields = Pick<
  ToolPermissionPreHookFormState,
  'toolMatch' | 'onTrue' | 'onFalse' | 'onError' | 'timeout' | 'retries'
>;

type ActionLabels = Record<'onTrue' | 'onFalse' | 'onError', string>;

const HTTP_ACTION_LABELS: ActionLabels = {
  onTrue: '服务返回 True 时',
  onFalse: '服务返回 False 时',
  onError: '服务异常时',
};

const AI_ACTION_LABELS: ActionLabels = {
  onTrue: '判定为 True 时',
  onFalse: '判定为 False 时',
  onError: '判定失败时',
};

/** 适用工具支持英文 / 中文逗号、顿号和空白分隔 */
function splitToolMatch(text: string): string[] {
  return text.split(/[,，、\s]+/).filter(Boolean);
}

/** 与后端一致：须带 http(s) 协议头且有 host */
function isHttpUrl(value: string): boolean {
  if (!/^https?:\/\//i.test(value)) return false;
  try {
    return new URL(value).host !== '';
  } catch {
    return false;
  }
}

const isAiHook = (hook: ToolPermissionPreHook) => hook.type === 'TOOL_PERMISSION_PRE_HOOK_TYPE_AI';

function parseJudgeFields(hook: ToolPermissionPreHook): JudgeFields {
  return {
    toolMatch: hook.tool_match.join(', '),
    onTrue: hook.on_true,
    // 后端未设置的动作留空，保存前须重新选择
    onFalse: hook.on_false ?? '',
    onError: hook.on_error ?? '',
    timeout: hook.timeout ?? 0,
    retries: hook.retries ?? 0,
  };
}

/** 后端 pre_hooks 中的 HTTP 钩子 → 表单态；AI 钩子由 parseAiApproval 处理 */
export function parseToolPreHooks(
  hooks: ToolPermissionPreHook[]
): ToolPermissionPreHookFormState[] {
  return hooks
    .filter((hook) => !isAiHook(hook))
    .map((hook) => ({
      name: hook.name,
      endpointUrl: hook.endpoint_url ?? '',
      ...parseJudgeFields(hook),
    }));
}

/** 后端 pre_hooks 中的 AI 钩子 → AI 审批表单态；没有 AI 钩子时为关闭状态 */
export function parseAiApproval(hooks: ToolPermissionPreHook[]): AiApprovalFormState {
  const hook = hooks.find(isAiHook);
  if (!hook) return { ...DEFAULT_AI_APPROVAL };
  return { enabled: true, prompt: hook.prompt ?? '', ...parseJudgeFields(hook) };
}

function buildJudgeFields(fields: JudgeFields) {
  return {
    on_true: fields.onTrue as ToolPermissionPreHookAction,
    on_false: fields.onFalse as ToolPermissionPreHookAction,
    tool_match: splitToolMatch(fields.toolMatch),
    timeout: fields.timeout,
    retries: fields.retries,
    on_error: fields.onError as ToolPermissionPreHookAction,
  };
}

/**
 * 表单态 → 后端 pre_hooks：HTTP 钩子按顺序在前，开启 AI 审批时在末尾追加 AI 钩子。
 * 须先通过 validateToolPreHooks（动作均已选择）。
 */
export function buildToolPreHooks(
  hooks: ToolPermissionPreHookFormState[],
  aiApproval: AiApprovalFormState
): ToolPermissionPreHook[] {
  const built: ToolPermissionPreHook[] = hooks.map((hook) => ({
    name: hook.name.trim(),
    // HTTP 是枚举零值，仍显式写出
    type: 'TOOL_PERMISSION_PRE_HOOK_TYPE_HTTP',
    endpoint_url: hook.endpointUrl.trim(),
    ...buildJudgeFields(hook),
  }));
  if (aiApproval.enabled) {
    built.push({
      name: AI_PRE_HOOK_NAME,
      type: 'TOOL_PERMISSION_PRE_HOOK_TYPE_AI',
      prompt: aiApproval.prompt.trim(),
      ...buildJudgeFields(aiApproval),
    });
  }
  return built;
}

/** 校验共有的判定字段；noAutoApprove 列出不能选自动批准的动作 */
function validateJudgeFields(
  fields: JudgeFields,
  labels: ActionLabels,
  noAutoApprove: (keyof ActionLabels)[]
): string | null {
  const tools = splitToolMatch(fields.toolMatch);
  if (tools.length === 0) return '请填写适用工具';
  if (tools.includes('*')) return '适用工具不支持 *';
  for (const key of ['onTrue', 'onFalse', 'onError'] as const) {
    if (!fields[key]) return `请选择${labels[key]}的动作`;
  }
  for (const key of noAutoApprove) {
    if (fields[key] === 'TOOL_PERMISSION_PRE_HOOK_ACTION_AUTO_APPROVE') {
      return `${labels[key]}不能自动批准`;
    }
  }
  if (fields.timeout < 0) return '超时不能为负数';
  if (fields.retries < 0) return '重试次数不能为负数';
  return null;
}

/** 校验前置钩子与 AI 审批，返回错误信息（null 表示通过）；规则与后端 ValidateToolPermissions 一致 */
export function validateToolPreHooks(
  hooks: ToolPermissionPreHookFormState[],
  aiApproval: AiApprovalFormState
): string | null {
  const positions = new Map<string, number>();
  for (const [i, hook] of hooks.entries()) {
    const label = `前置钩子 ${i + 1}`;
    const name = hook.name.trim();
    if (!name) return `${label}：请填写名称`;
    const duplicate = positions.get(name);
    if (duplicate !== undefined) return `${label}：名称「${name}」与前置钩子 ${duplicate} 重复`;
    // AI 钩子固定叫 ai，同名会被后端判为重复
    if (aiApproval.enabled && name === AI_PRE_HOOK_NAME) {
      return `${label}：名称「${AI_PRE_HOOK_NAME}」已用于 AI 审批`;
    }
    positions.set(name, i + 1);

    const endpointUrl = hook.endpointUrl.trim();
    if (!endpointUrl) return `${label}：请填写 URL`;
    if (!isHttpUrl(endpointUrl)) return `${label}：URL 必须是 http 或 https 地址`;

    const error = validateJudgeFields(hook, HTTP_ACTION_LABELS, ['onError']);
    if (error) return `${label}：${error}`;
  }
  if (aiApproval.enabled) {
    // 模型拿不准时判为 False，False 自动批准等于放行所有拿不准的调用
    const error = validateJudgeFields(aiApproval, AI_ACTION_LABELS, ['onFalse', 'onError']);
    if (error) return `AI 审批：${error}`;
  }
  return null;
}

/**
 * 默认行为为自动批准时的放行风险提示（与后端一致，只提示不拦截）：
 * 所有钩子判定失败都继续判定时，判定方不可用会放行所有调用；AI 判定为 False 时继续判定，模型拿不准的调用会被放行。
 */
export function toolPreHookRiskWarnings(
  hooks: ToolPermissionPreHookFormState[],
  aiApproval: AiApprovalFormState,
  defaultAutoApprove: boolean
): string[] {
  if (!defaultAutoApprove) return [];
  const fallthrough = 'TOOL_PERMISSION_PRE_HOOK_ACTION_FALLTHROUGH';
  const warnings: string[] = [];
  const onErrors = hooks.map((hook) => hook.onError);
  if (aiApproval.enabled) onErrors.push(aiApproval.onError);
  if (onErrors.length > 0 && onErrors.every((action) => action === fallthrough)) {
    warnings.push(
      '所有前置钩子在判定失败（服务异常）时都选择了「继续判定」，且默认行为为「自动批准」：判定方不可用时会放行所有工具调用。建议至少一个钩子在判定失败时选择「拒绝」或「审批」，或修改默认行为。'
    );
  }
  if (aiApproval.enabled && aiApproval.onFalse === fallthrough) {
    warnings.push(
      'AI 审批在判定为 False 时选择了「继续判定」，且默认行为为「自动批准」：模型拿不准的调用在后续规则都未命中时会被自动放行。建议改为「审批」或「拒绝」。'
    );
  }
  return warnings;
}
